import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  InputAdornment,
  Divider,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import PercentIcon from "@mui/icons-material/Percent";
import TrackChangesIcon from "@mui/icons-material/TrackChanges";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import PersonIcon from "@mui/icons-material/Person";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";

const SettingsPanel = ({
  settings,
  onSaveSettings,
  onRestoreDefaults,
  users,
  onSaveUsers,
  availableCollaborators,
}) => {
  const [localSettings, setLocalSettings] = useState(settings);
  const [localUsers, setLocalUsers] = useState(users);
  const [activeTab, setActiveTab] = useState("kpi");

  const [newUser, setNewUser] = useState({
    username: "",
    role: "Collaborator",
    password: "",
    linkedCollaborator: "",
  });

  // Update local state if settings prop changes (e.g. from restore default)
  useEffect(() => {
    setLocalSettings(settings);
  }, [settings]);

  const handleSave = () => {
    onSaveSettings(localSettings);
    onSaveUsers(localUsers);
    alert("Impostazioni e Utenti salvati con successo!");
  };

  const addUser = () => {
    if (!newUser.username || !newUser.password) {
      alert("Username e Password sono obbligatori");
      return;
    }
    if (localUsers.find((u) => u.username === newUser.username)) {
      alert("Username già esistente");
      return;
    }
    setLocalUsers([...localUsers, newUser]);
    setNewUser({
      username: "",
      role: "Collaborator",
      password: "",
      linkedCollaborator: "",
    });
  };

  const removeUser = (username) => {
    if (username === "admin") {
      alert("Non è possibile eliminare l'utente amministratore predefinito");
      return;
    }
    setLocalUsers(localUsers.filter((u) => u.username !== username));
  };

  const updateUserPassword = (username, newPass) => {
    setLocalUsers(
      localUsers.map((u) =>
        u.username === username ? { ...u, password: newPass } : u,
      ),
    );
  };

  return (
    <Paper
      sx={{
        borderRadius: 4,
        overflow: "hidden",
        boxShadow: 4,
        border: 1,
        borderColor: "grey.200",
      }}
    >
      <Box
        sx={{
          bgcolor: "secondary.main",
          p: 3,
          color: "common.white",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 900,
              textTransform: "uppercase",
              letterSpacing: "-0.05em",
            }}
          >
            Pannello Controllo Admin
          </Typography>
          <Typography
            sx={{
              fontSize: "0.625rem",
              color: "grey.400",
              fontWeight: "bold",
              textTransform: "uppercase",
              letterSpacing: "0.1em",
              mt: 0.5,
            }}
          >
            Configurazione Engine MeritX
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={() => setActiveTab("kpi")}
            variant={activeTab === "kpi" ? "contained" : "text"}
            color={activeTab === "kpi" ? "primary" : "inherit"}
            sx={{
              borderRadius: 3,
              fontSize: "0.625rem",
              fontWeight: 900,
              textTransform: "uppercase",
              color: activeTab === "kpi" ? "common.white" : "grey.400",
              "&:hover": {
                color: "common.white",
                bgcolor:
                  activeTab === "kpi"
                    ? "primary.dark"
                    : "rgba(255,255,255,0.1)",
              },
            }}
          >
            Parametri KPI
          </Button>
          <Button
            onClick={() => setActiveTab("users")}
            variant={activeTab === "users" ? "contained" : "text"}
            color={activeTab === "users" ? "primary" : "inherit"}
            sx={{
              borderRadius: 3,
              fontSize: "0.625rem",
              fontWeight: 900,
              textTransform: "uppercase",
              color: activeTab === "users" ? "common.white" : "grey.400",
              "&:hover": {
                color: "common.white",
                bgcolor:
                  activeTab === "users"
                    ? "primary.dark"
                    : "rgba(255,255,255,0.1)",
              },
            }}
          >
            Gestione Accessi
          </Button>
        </Box>
      </Box>

      <Box sx={{ p: 4, maxHeight: "70vh", overflowY: "auto" }}>
        {activeTab === "kpi" ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                onClick={onRestoreDefaults}
                startIcon={<RestoreIcon />}
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  color: "grey.400",
                  "&:hover": { color: "primary.main" },
                }}
              >
                Ripristina Default
              </Button>
            </Box>
            <Box component="section">
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 900,
                  color: "grey.400",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <PercentIcon sx={{ color: "primary.main", fontSize: "1rem" }} />{" "}
                Provvigioni & Commissioni
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Tasso Non-Motor"
                    value={localSettings.commissions.nonMotorRate}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        commissions: {
                          ...localSettings.commissions,
                          nonMotorRate: v,
                        },
                      })
                    }
                    isPerc
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Motor 0 Garanzie"
                    value={localSettings.commissions.motor0Gar}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        commissions: {
                          ...localSettings.commissions,
                          motor0Gar: v,
                        },
                      })
                    }
                    isPerc
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Motor 1 Garanzia"
                    value={localSettings.commissions.motor1Gar}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        commissions: {
                          ...localSettings.commissions,
                          motor1Gar: v,
                        },
                      })
                    }
                    isPerc
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Motor 2 Garanzie"
                    value={localSettings.commissions.motor2Gar}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        commissions: {
                          ...localSettings.commissions,
                          motor2Gar: v,
                        },
                      })
                    }
                    isPerc
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Motor 3+ Garanzie"
                    value={localSettings.commissions.motor3PlusGar}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        commissions: {
                          ...localSettings.commissions,
                          motor3PlusGar: v,
                        },
                      })
                    }
                    isPerc
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Bonus Flat Auto 3G+"
                    value={localSettings.commissions.bonusFlatAuto3Gar}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        commissions: {
                          ...localSettings.commissions,
                          bonusFlatAuto3Gar: v,
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "grey.100" }} />

            <Box component="section">
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 900,
                  color: "grey.400",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <TrackChangesIcon
                  sx={{ color: "info.main", fontSize: "1rem" }}
                />{" "}
                Target Meritocratici
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Target Mix Non-Motor"
                    value={localSettings.targets.mixNonMotor}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        targets: { ...localSettings.targets, mixNonMotor: v },
                      })
                    }
                    isPerc
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Garanzie Nuove (Min)"
                    value={localSettings.targets.garMotorNew}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        targets: { ...localSettings.targets, garMotorNew: v },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Garanzie Rinnovo (Min)"
                    value={localSettings.targets.garMotorRen}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        targets: { ...localSettings.targets, garMotorRen: v },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Ritenzione Base (%)"
                    value={localSettings.targets.retentionBase}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        targets: { ...localSettings.targets, retentionBase: v },
                      })
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Ritenzione Super (%)"
                    value={localSettings.targets.retentionSuper}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        targets: {
                          ...localSettings.targets,
                          retentionSuper: v,
                        },
                      })
                    }
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider sx={{ borderColor: "grey.100" }} />

            <Box component="section">
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 900,
                  color: "grey.400",
                  textTransform: "uppercase",
                  letterSpacing: "0.1em",
                  mb: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <EmojiEventsIcon
                  sx={{ color: "success.main", fontSize: "1rem" }}
                />{" "}
                Percentuali Bonus Volume
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Bonus Livello 1 (Qualità)"
                    value={localSettings.bonusPerc.level1 * 100}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        bonusPerc: {
                          ...localSettings.bonusPerc,
                          level1: v / 100,
                        },
                      })
                    }
                    isPerc
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <InputGroup
                    label="Bonus Livello 2 (Eccellenza)"
                    value={localSettings.bonusPerc.level2 * 100}
                    onChange={(v) =>
                      setLocalSettings({
                        ...localSettings,
                        bonusPerc: {
                          ...localSettings.bonusPerc,
                          level2: v / 100,
                        },
                      })
                    }
                    isPerc
                  />
                </Grid>
              </Grid>
            </Box>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 4,
                border: 1,
                borderColor: "grey.200",
                boxShadow: 0,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  color: "grey.500",
                  mb: 2,
                }}
              >
                Aggiungi Nuovo Utente
              </Typography>
              <Grid container spacing={2} alignItems="flex-end">
                <Grid item xs={12} md={2.4}>
                  <TextField
                    fullWidth
                    label="Username"
                    variant="outlined"
                    size="small"
                    value={newUser.username}
                    onChange={(e) =>
                      setNewUser({ ...newUser, username: e.target.value })
                    }
                    placeholder="Es. mario.rossi"
                    InputLabelProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: "0.75rem",
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Password"
                    variant="outlined"
                    size="small"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    placeholder="••••••••"
                    InputLabelProps={{
                      sx: {
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      },
                    }}
                    InputProps={{
                      sx: {
                        fontSize: "0.75rem",
                        borderRadius: 2,
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <FormControl fullWidth size="small">
                    <InputLabel
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      Ruolo
                    </InputLabel>
                    <Select
                      value={newUser.role}
                      label="Ruolo"
                      onChange={(e) =>
                        setNewUser({ ...newUser, role: e.target.value })
                      }
                      sx={{
                        fontSize: "0.75rem",
                        borderRadius: 2,
                      }}
                    >
                      <MenuItem value="Admin" sx={{ fontSize: "0.75rem" }}>
                        Admin
                      </MenuItem>
                      <MenuItem
                        value="Collaborator"
                        sx={{ fontSize: "0.75rem" }}
                      >
                        Collaboratore
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={newUser.role !== "Collaborator"}
                  >
                    <InputLabel
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    >
                      Collab. Associato
                    </InputLabel>
                    <Select
                      value={newUser.linkedCollaborator}
                      label="Collab. Associato"
                      onChange={(e) =>
                        setNewUser({
                          ...newUser,
                          linkedCollaborator: e.target.value,
                        })
                      }
                      sx={{
                        fontSize: "0.75rem",
                        borderRadius: 2,
                      }}
                    >
                      <MenuItem value="" sx={{ fontSize: "0.75rem" }}>
                        <em>Seleziona...</em>
                      </MenuItem>
                      {availableCollaborators.map((c) => (
                        <MenuItem
                          key={c}
                          value={c}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {c}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2.4}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={addUser}
                    sx={{
                      py: 1,
                      fontSize: "0.625rem",
                      fontWeight: 900,
                      textTransform: "uppercase",
                      borderRadius: 2,
                      "&:hover": { bgcolor: "primary.main" },
                    }}
                  >
                    Aggiungi
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography
                sx={{
                  fontSize: "0.625rem",
                  fontWeight: 900,
                  textTransform: "uppercase",
                  color: "grey.500",
                }}
              >
                Utenti Registrati
              </Typography>
              <Paper
                sx={{
                  borderRadius: 4,
                  border: 1,
                  borderColor: "grey.100",
                  boxShadow: 0,
                  overflow: "hidden",
                }}
              >
                {localUsers.map((u, index) => (
                  <Box
                    key={u.username}
                    sx={{
                      p: 2,
                      display: "flex",
                      flexDirection: { xs: "column", md: "row" },
                      justifyContent: "space-between",
                      alignItems: { md: "center" },
                      gap: 2,
                      borderBottom: index < localUsers.length - 1 ? 1 : 0,
                      borderColor: "grey.100",
                      "&:hover": { bgcolor: "grey.50" },
                      transition: "background-color 0.2s",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        minWidth: 200,
                      }}
                    >
                      <Box
                        sx={{
                          width: 32,
                          height: 32,
                          borderRadius: 2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "common.white",
                          boxShadow: 1,
                          bgcolor:
                            u.role === "Admin" ? "info.main" : "primary.main",
                        }}
                      >
                        {u.role === "Admin" ? (
                          <AdminPanelSettingsIcon fontSize="small" />
                        ) : (
                          <PersonIcon fontSize="small" />
                        )}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 900,
                            textTransform: "uppercase",
                            color: "text.primary",
                          }}
                        >
                          {u.username}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.5625rem",
                            fontWeight: "bold",
                            color: "text.disabled",
                            textTransform: "uppercase",
                          }}
                        >
                          {u.role}{" "}
                          {u.linkedCollaborator
                            ? `| ${u.linkedCollaborator}`
                            : ""}
                        </Typography>
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flex: 1,
                        justifyContent: { md: "flex-end" },
                      }}
                    >
                      <TextField
                        type="password"
                        label="Pass"
                        size="small"
                        value={u.password || ""}
                        onChange={(e) =>
                          updateUserPassword(u.username, e.target.value)
                        }
                        placeholder="Password"
                        InputLabelProps={{
                          sx: {
                            fontSize: "0.625rem",
                            fontWeight: 900,
                            textTransform: "uppercase",
                            color: "grey.400",
                          },
                          shrink: true,
                        }}
                        InputProps={{
                          sx: {
                            fontSize: "0.625rem",
                            fontWeight: "bold",

                            borderRadius: 2,
                            width: 120,
                          },
                        }}
                      />
                      <IconButton
                        onClick={() => removeUser(u.username)}
                        sx={{
                          color: "grey.300",
                          "&:hover": { color: "error.main" },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "flex-end",
          gap: 1.5,
          borderTop: 1,
          borderColor: "grey.200",
        }}
      >
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          startIcon={<SaveIcon />}
          sx={{
            px: 4,
            py: 1.5,
            borderRadius: 4,
            fontSize: "0.75rem",
            fontWeight: 900,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            boxShadow:
              "0 10px 15px -3px rgba(16, 185, 129, 0.1), 0 4px 6px -2px rgba(16, 185, 129, 0.05)",
          }}
        >
          Salva Configurazioni
        </Button>
      </Box>
    </Paper>
  );
};

const InputGroup = ({ label, value, onChange, isPerc }) => (
  <TextField
    fullWidth
    label={label}
    type="number"
    variant="outlined"
    size="small"
    value={value}
    onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
    inputProps={{ step: isPerc ? "0.01" : "1" }}
    InputLabelProps={{
      sx: {
        fontSize: "0.625rem",
        fontWeight: "bold",
        textTransform: "uppercase",
        color: "grey.500",
        letterSpacing: "-0.05em",
      },
    }}
    InputProps={{
      sx: {
        fontSize: "0.75rem",
        fontWeight: "bold",
        borderRadius: 3,
      },
      endAdornment: (
        <InputAdornment position="end">
          <Typography
            sx={{ fontSize: "0.625rem", fontWeight: 900, color: "grey.300" }}
          >
            {isPerc ? "%" : "€"}
          </Typography>
        </InputAdornment>
      ),
    }}
  />
);

export default SettingsPanel;
